/**
 * GameErrorBoundary Component
 *
 * A specialized Error Boundary for game components with additional features:
 * - Retry functionality with automatic retry count
 * - Game-specific error messages
 * - Option to return to game lobby
 * - Preservation of game ID for recovery
 *
 * Usage:
 * <GameErrorBoundary gameId={gameId} gameName="MferChess" lobbyPath="/mferchess/">
 *   <GameComponent />
 * </GameErrorBoundary>
 */

import React, { Component, ReactNode, ErrorInfo } from 'react';
import logger from '../utils/logger';
import './GameErrorBoundary.css';

interface GameErrorBoundaryProps {
  children: ReactNode;
  gameId?: string;
  gameName?: string;
  lobbyPath?: string;
  maxRetries?: number;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface BuildInfo {
  hash: string;
  timestamp: string;
}

interface GameErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  buildInfo: BuildInfo | null;
}

class GameErrorBoundary extends Component<GameErrorBoundaryProps, GameErrorBoundaryState> {
  state: GameErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
    retryCount: 0,
    buildInfo: null,
  };

  constructor(props: GameErrorBoundaryProps) {
    super(props);
  }

  componentDidMount() {
    // Fetch build info
    fetch('/build-info.json')
      .then(res => res.json())
      .then(data => this.setState({ buildInfo: data }))
      .catch(() => {
        // In development, build-info.json might not exist
        this.setState({ buildInfo: { hash: 'dev', timestamp: new Date().toISOString() } });
      });
  }

  static getDerivedStateFromError(error: Error): Partial<GameErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, gameId, gameName } = this.props;

    // Log the error with game context
    logger.error('Game Error Boundary caught an error', {
      error: {
        message: error.message,
        name: error.name,
        stack: error.stack,
      },
      errorInfo: {
        componentStack: errorInfo.componentStack,
      },
      game: {
        id: gameId,
        name: gameName,
      },
      retryCount: this.state.retryCount,
      timestamp: new Date().toISOString(),
    }, 'game-error');

    // Store error info in state
    this.setState({
      errorInfo,
    });

    // Call custom error handler if provided
    if (onError) {
      try {
        onError(error, errorInfo);
      } catch (handlerError) {
        logger.error('Error in game error handler', {
          error: handlerError,
        }, 'game-error');
      }
    }
  }

  handleRetry = (): void => {
    const { maxRetries = 3 } = this.props;
    const { retryCount } = this.state;

    if (retryCount < maxRetries) {
      logger.info('Game error boundary retry', {
        retryCount: retryCount + 1,
        maxRetries,
      }, 'game-error');

      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: retryCount + 1,
      });
    } else {
      logger.warn('Game error boundary max retries reached', {
        retryCount,
        maxRetries,
      }, 'game-error');
    }
  };

  handleGoToLobby = (): void => {
    const { lobbyPath } = this.props;
    if (lobbyPath) {
      window.location.href = lobbyPath;
    } else {
      window.location.href = '/';
    }
  };

  formatBuildInfo = (): string => {
    const { buildInfo } = this.state;
    if (!buildInfo) return 'Unknown';

    const date = new Date(buildInfo.timestamp);
    const formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });

    return `${buildInfo.hash.substring(0, 7)} (${formattedDate} ${formattedTime})`;
  };

  render() {
    const { hasError, error, errorInfo, retryCount } = this.state;
    const { children, gameName = 'Game', lobbyPath, maxRetries = 3 } = this.props;

    if (hasError && error) {
      const canRetry = retryCount < maxRetries;
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div className="game-error-boundary">
          <div className="game-error-content">
            <div className="game-error-icon">🎮</div>
            <h2 className="game-error-title">Game Error</h2>
            <p className="game-error-message">
              {gameName} encountered an unexpected error.
              {canRetry ? ' You can try restarting the game.' : ' Maximum retry attempts reached.'}
            </p>

            {retryCount > 0 && (
              <p className="game-error-retry-info">
                Retry attempts: {retryCount} / {maxRetries}
              </p>
            )}

            <div className="game-error-actions">
              {canRetry && (
                <button
                  onClick={this.handleRetry}
                  className="game-error-button game-error-button-primary"
                >
                  Restart Game
                </button>
              )}
              {lobbyPath && (
                <button
                  onClick={this.handleGoToLobby}
                  className="game-error-button game-error-button-secondary"
                >
                  Back to Lobby
                </button>
              )}
              <button
                onClick={() => window.location.href = '/'}
                className="game-error-button game-error-button-secondary"
              >
                Home
              </button>
            </div>

            <div className="game-error-info">
              <p className="game-error-info-text">
                If this problem persists, please report it using the bug report button.
              </p>
            </div>

            {isDevelopment && (
              <details className="game-error-details">
                <summary className="game-error-details-summary">
                  Error Details (Development Only)
                </summary>
                <div className="game-error-details-content">
                  <div className="game-error-error-info">
                    <h3>Build Version</h3>
                    <pre>{this.formatBuildInfo()}</pre>

                    <h3>Error Message</h3>
                    <pre>{error.message}</pre>

                    <h3>Stack Trace</h3>
                    <pre>{error.stack}</pre>

                    {errorInfo && (
                      <>
                        <h3>Component Stack</h3>
                        <pre>{errorInfo.componentStack}</pre>
                      </>
                    )}

                    <h3>Game Context</h3>
                    <pre>{JSON.stringify({
                      gameName,
                      gameId: this.props.gameId,
                      lobbyPath,
                      retryCount,
                      maxRetries,
                    }, null, 2)}</pre>
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return children;
  }
}

export default GameErrorBoundary;
